import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { Text } from '@shared/components/Text';
import { ButtonView, View } from '@shared/components/View';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { ReactionButtonWithPicker } from './ReactionButtonWithPicker';
import { getReactionEmoji } from './types';
import styles from './ReactionRow.module.css';

export interface ReactionRowProps {
  messageId: number;
  // List of all current reactions users have made.
  reactions: API.ReactionFragment[];
  align: 'left' | 'right';
  selected: API.ReactionType[];
  onToggleReaction: (reaction: API.ReactionType, add: boolean) => void;
  onClickReaction: VoidFunction;
  anchorName?: string;
}

/*
    List of stacked reactions. Also shows total reaction count and a button
    to add/remove reactions.
*/
export function ReactionRow({
  reactions,
  align,
  selected,
  onClickReaction,
  onToggleReaction,
  anchorName,
  messageId,
}: ReactionRowProps) {
  const reactionButtonAnchorName = getAnchorName('reaction-button-stacked', messageId);

  const reactionTypes = useMemo(
    () => Array.from(new Set(reactions.map(reaction => reaction.type))),
    [reactions],
  );

  if (isEmpty(reactionTypes)) {
    return null;
  }

  return (
    <View
      row={align === 'right' ? 'reverse' : true}
      wrap
      centerVertically
      testID="stacked-reactions"
      gap={0.75}
    >
      <View className={styles.reactionRowItems} style={{ anchorName }}>
        {reactionTypes.map(type => (
          <ReactionRowItem
            key={type}
            type={type}
            reactions={reactions}
            isSelected={selected.includes(type)}
            onClickReaction={onClickReaction}
          />
        ))}
      </View>
      <ReactionButtonWithPicker
        anchorName={reactionButtonAnchorName}
        selected={selected}
        onToggleReaction={onToggleReaction}
      />
    </View>
  );
}

function ReactionRowItem({
  type,
  reactions,
  isSelected,
  onClickReaction,
}: {
  type: API.ReactionType;
  reactions: API.ReactionFragment[];
  isSelected: boolean;
  onClickReaction: VoidFunction;
}) {
  const count = reactions.filter(reaction => reaction.type === type).length;
  const emoji = getReactionEmoji(type);

  return (
    <ButtonView
      className={[styles.reactionRowItem, isSelected && styles.selected]}
      onClick={onClickReaction}
    >
      <Text className={styles.emoji}>{emoji}</Text>
      <Text size="overline" weight="semibold" className={styles.count}>
        {count}
      </Text>
    </ButtonView>
  );
}
